import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const sources = [
  "Price Data",
  "Options Flow",
  "Reddit Sentiment",
  "Twitter Sentiment",
  "Dark Pool Feeds",
  "Politician Trades",
  "SEC Filings",
  "Analyst Ratings",
  "Fundamentals",
  "Gamma Exposure",
  "IV Data",
];

export const Greeting = () => {
  const [index, setIndex] = useState(0);
  const [widestSource, setWidestSource] = useState(sources[0]);
  const measureRef = useRef<HTMLSpanElement>(null);
  // Hide the "Introducing Autopilot" pill for signed-in users — it's pure
  // marketing for new visitors. Logged-in users already know what Xynth
  // is and seeing a product-launch pill in their own chat landing feels
  // like spam. Signed-out visitors (landing page) still see it.
  const { user } = useAuth();
  const showAutopilotPill = !user;

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % sources.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateSlotWidth = () => {
      if (!measureRef.current) return;

      let max = 0;
      const spans = measureRef.current.children;
      let nextWidestSource = sources[0];

      for (let i = 0; i < spans.length; i++) {
        const width = (spans[i] as HTMLElement).offsetWidth;

        if (width > max) {
          max = width;
          nextWidestSource = sources[i];
        }
      }

      setWidestSource(nextWidestSource);
    };

    updateSlotWidth();
    window.addEventListener("resize", updateSlotWidth);

    return () => window.removeEventListener("resize", updateSlotWidth);
  }, []);

  return (
    <div className="mx-auto px-4 text-center max-w-3xl">
      {/* ── Product launch pill ─────────────────────────────────────────────
          Editorial-styled announcement for the new Autopilot product line.
          Deliberately understated so it feels like a "new product launched"
          pill from Linear / Vercel / Stripe — not a loud AI-marketing banner.

          Styling notes (chic / rich / professional):
          - Dim slate surface with hairline border and backdrop blur (glassy)
          - "NEW" label in small-caps with letter-spacing (editorial typography)
          - Tiny emerald dot is the ONLY brand-color accent — pulsing to imply
            "live"
          - Arrow icon nudges right on hover
          - Click smooth-scrolls to the real Autopilot landing section.        */}
      {showAutopilotPill && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mt-3 mb-4 flex justify-center sm:mt-4 sm:mb-5 md:mt-6 md:mb-6"
        >
          <a
            href="/pricing"
            aria-label="Try Xynth free, no credit card required"
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[12px] font-medium text-white/80 backdrop-blur-sm transition-colors duration-200 hover:border-white/20 hover:bg-white/[0.06] hover:text-white sm:gap-3 sm:px-5 sm:py-2.5 sm:text-[13px]"
          >
            {/* Pulsing live-dot — the only brand color on the pill */}
            <span className="relative inline-flex h-1.5 w-1.5 shrink-0 sm:h-[7px] sm:w-[7px]">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60" />
              <span className="relative inline-flex h-full w-full rounded-full bg-emerald-400" />
            </span>

            <span className="font-semibold uppercase tracking-[0.16em] text-white/55 group-hover:text-white/75">
              Free
            </span>

            <span aria-hidden className="h-3.5 w-px bg-white/15" />

            <span className="tracking-tight">Try free, no credit card</span>

            <ArrowUpRight
              className="h-4 w-4 text-white/50 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white/80"
              strokeWidth={2}
            />
          </a>
        </motion.div>
      )}

      <h1 className="whitespace-nowrap text-[1.4rem] leading-[1] font-semibold tracking-[-0.04em] text-white sm:text-[2.25rem] md:text-[3.1rem] md:leading-[1]">
        Find{" "}
        <span
          style={{ fontFamily: "var(--font-fraunces)", fontOpticalSizing: "auto" }}
          className="relative inline-block px-[0.04em] font-medium italic tracking-[-0.015em] text-emerald-400 drop-shadow-[0_0_10px_rgba(52,211,153,0.22)]"
        >
          winning
          <svg
            aria-hidden
            viewBox="0 0 100 8"
            preserveAspectRatio="none"
            className="pointer-events-none absolute left-0 right-0 -bottom-[0.18em] h-[0.28em] w-full text-emerald-400"
          >
            <path
              d="M 1 6 Q 50 -1 99 6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </span>{" "}
        trades with AI
      </h1>

      <span ref={measureRef} aria-hidden className="absolute opacity-0 pointer-events-none whitespace-nowrap text-[0.92rem] font-semibold sm:text-[1.02rem] md:text-[1.12rem]">
        {sources.map((source) => (
          <span key={source} className="inline-block">{source}</span>
        ))}
      </span>

      <div className="mx-auto mt-5 flex flex-col items-center gap-1.5 text-[0.92rem] font-normal leading-tight text-white/75 sm:text-[1.02rem] md:text-[1.12rem]">
        <p className="flex flex-wrap items-baseline justify-center gap-x-1.5">
          <span>Your AI market analyst for </span>
          <span className="relative inline-block align-baseline">
            <span aria-hidden className="invisible whitespace-nowrap font-semibold text-white">
              {widestSource}
            </span>
            <span className="absolute inset-0 overflow-hidden">
              <AnimatePresence initial={false} mode="wait">
                <motion.span
                  key={sources[index]}
                  initial={{ y: 8, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -8, opacity: 0 }}
                  transition={{ duration: 0.28, ease: "easeInOut" }}
                  className="absolute left-0 bottom-0 whitespace-nowrap font-semibold text-white"
                >
                  {sources[index]}
                </motion.span>
              </AnimatePresence>
            </span>
          </span>
        </p>
      </div>
    </div>
  );
};
